import classNames from "classnames";
import { CategoryIcon } from "components/CategoryIcon";
import { Image } from "components/Image";
import { useTracking } from "hooks/useTracking";
import { split } from "lib/util";
import { Breadcrumb } from "./Breadcrumb";
import { Section } from "./Section/Section";

function isDescription(
  section: Travelguide.OOISection
): section is Travelguide.POISection.Description {
  return section.type === "description";
}

function isAside(section: Travelguide.OOISection) {
  return Boolean(section.aside);
}

export const Detail = ({
  item,
  sections,
}: {
  item: Travelguide.OOI;
  sections: Array<Travelguide.OOISection>;
}) => {
  const renderSection = (section: Travelguide.OOISection, i: number) => (
    <Section key={i} item={item} section={section} />
  );
  const renderSectionFull = (section: Travelguide.OOISection, i: number) => (
    <Section key={i} item={item} section={section} full />
  );
  const [otherSections, description] = split(sections, isDescription);
  const [main, aside] = otherSections.some(isAside)
    ? split(otherSections, isAside)
    : [otherSections];

  const categoryIconSize = "w-3/12 md:w-28";

  useTracking(item.id);

  return (
    <article
      className="flex flex-col gap-6 md:gap-20"
      itemScope
      itemType="http://schema.org/Place"
    >
      <header className="flex flex-col gap-6">
        <div
          className={classNames(
            "flex items-center justify-center",
            "aspect-detail-head-sm md:aspect-detail-head-md overflow-hidden"
          )}
        >
          <Image
            image={item.primaryImage}
            bakeSettings={{ width: 1600, height: 900 }}
            fill
          />
          <CategoryIcon
            className={classNames(categoryIconSize, "absolute md:hidden")}
            category={item.category}
          />
        </div>
        <Breadcrumb item={item} />
        <h1 className="px-4 md:px-6">
          <CategoryIcon
            className={classNames(categoryIconSize, "hidden md:block")}
            category={item.category}
          />
          <span className="md:max-w-sm md:text-center" itemProp="name">
            {item.title}
          </span>
        </h1>
      </header>
      <main className={"grid grid-flow-row gap-8 md:gap-20"}>
        {Array.isArray(aside) && aside.length ? (
          <>
            <div
              className={classNames(
                "grid gap-x-10 gap-y-6",
                "md:grid-cols-[1fr,minmax(20rem,min-content)] md:grid-rows-[auto,1fr]",
                "md:px-6"
              )}
            >
              {description.map(renderSectionFull)}
              <aside className="md:row-start-1 md:col-start-2 md:row-span-2">
                {aside.map(renderSection)}
              </aside>
            </div>
            {main.map(renderSection)}
          </>
        ) : (
          <>
            {description.map(renderSection)}
            {main.map(renderSection)}
          </>
        )}
      </main>
    </article>
  );
};
